import { supabase } from '../supabaseClient'

const ALERT_COOLDOWN_KEY = 'audit_alert_last_sent'
const ALERT_COOLDOWN_MS = 60 * 60 * 1000 // max one alert email per hour

async function sendAuditFailureAlert(action, error) {
  try {
    const last = parseInt(localStorage.getItem(ALERT_COOLDOWN_KEY) || '0', 10)
    if (Date.now() - last < ALERT_COOLDOWN_MS) return

    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return

    localStorage.setItem(ALERT_COOLDOWN_KEY, String(Date.now()))

    await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/alert-audit-failure`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action,
        errorMessage: error?.message || String(error),
        userEmail: session.user?.email,
      }),
    })
  } catch {
    // alert failure must never throw
  }
}

/**
 * Log an action to the audit log
 * @param {string} action - The action type (e.g., 'user_created', 'user_updated')
 * @param {object} details - Additional details about the action
 * @param {object} user - The user performing the action
 */
export async function logAuditEvent(action, details = {}, user = null) {
  try {
    // Get current user if not provided
    if (!user) {
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      if (!currentUser) return

      // Get user details from users table by email (since auth.uid may not match users.id)
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, organization_id')
        .eq('email', currentUser.email)
        .single()

      // If user not found in users table, skip audit logging
      if (userError || !userData) {
        console.warn('User not found in users table, skipping audit log')
        return
      }

      user = {
        id: userData.id,
        email: userData.email || currentUser.email,
        name: `${userData.first_name} ${userData.last_name}`,
        organization_id: userData.organization_id
      }
    }

    // Insert audit log
    const { error } = await supabase
      .from('audit_logs')
      .insert([{
        action,
        user_id: user.id,
        user_email: user.email,
        user_name: user.name,
        organization_id: user.organization_id,
        details,
        ip_address: null
      }])

    if (error) {
      console.error('Error logging audit event:', error)
      sendAuditFailureAlert(action, error)
    }
  } catch (err) {
    console.error('Error in logAuditEvent:', err)
    sendAuditFailureAlert(action, err)
  }
}

/**
 * Action type constants for consistency
 */
export const AUDIT_ACTIONS = {
  // User actions
  USER_CREATED: 'user_created',
  USER_UPDATED: 'user_updated',
  USER_DELETED: 'user_deleted',
  USER_ACTIVATED: 'user_activated',
  USER_DEACTIVATED: 'user_deactivated',
  
  // Establishment actions
  ESTABLISHMENT_CREATED: 'establishment_created',
  ESTABLISHMENT_UPDATED: 'establishment_updated',
  ESTABLISHMENT_ARCHIVED: 'establishment_archived',
  ESTABLISHMENT_DELETED: 'establishment_deleted',
  ESTABLISHMENT_RESTORED: 'establishment_restored',
  BULK_ASSIGNMENT: 'bulk_assignment',

  // Inspection actions
  INSPECTION_CREATED: 'inspection_created',
  INSPECTION_UPDATED: 'inspection_updated',
  INSPECTION_DELETED: 'inspection_deleted',

  // Complaint actions
  COMPLAINT_CREATED: 'complaint_created',
  COMPLAINT_UPDATED: 'complaint_updated',
  COMPLAINT_DELETED: 'complaint_deleted',

  // Schedule actions
  SCHEDULE_SHIFT_CREATED: 'schedule_shift_created',
  SCHEDULE_SHIFT_UPDATED: 'schedule_shift_updated',
  SCHEDULE_SHIFT_DELETED: 'schedule_shift_deleted',
  SCHEDULE_BULK_SHIFT_APPLIED: 'schedule_bulk_shift_applied',
  SCHEDULE_DAY_NOTE_ADDED: 'schedule_day_note_added',
  SCHEDULE_IMPORTED: 'schedule_imported',
  SCHEDULE_EXPORTED: 'schedule_exported',

  // Data actions
  DATA_IMPORTED: 'data_imported',
  DATA_EXPORTED: 'data_exported',

  // Till Detective
  TILL_ANALYSIS_CREATED: 'till_analysis_created',
  TILL_ANALYSIS_DELETED: 'till_analysis_deleted',
  TILL_REPORT_VIEWED: 'till_report_viewed',
  
  // Report actions
  REPORT_GENERATED: 'report_generated',
  
  // Campaign actions
  CAMPAIGN_CREATED: 'campaign_created',
  CAMPAIGN_STATUS_CHANGED: 'campaign_status_changed',

  // Photo actions
  PHOTO_UPLOADED: 'photo_uploaded',
  PHOTO_DELETED: 'photo_deleted',

  // System actions
  LOGIN: 'login',
  LOGIN_FAILED: 'login_failed',
  LOGOUT: 'logout',
  SETTINGS_CHANGED: 'settings_changed',

  // Priority inspection actions
  PRIORITY_SET:     'priority_set',
  PRIORITY_CLEARED: 'priority_cleared',

  // Payment receipt actions
  PAYMENT_RECEIPT_CREATED: 'payment_receipt_created',
  PAYMENT_RECEIPT_DELETED: 'payment_receipt_deleted',

  // PII Access actions (for security/compliance tracking)
  PII_DRIVERS_LICENSE_VIEWED: 'pii_drivers_license_viewed',
  PII_SSN_VIEWED: 'pii_ssn_viewed',
  PII_SENSITIVE_DATA_VIEWED: 'pii_sensitive_data_viewed'
}

